import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";

import { get, post, put } from "../../Services/ApiRequest";
import { API_URLS } from "../../Utils/Constants/APIConstants";
import {
  CadenceSchedule,
  DEFAULT_CADENCE_SCHEDULE,
} from "../../Utils/Guidance/guidanceCadence";
import {
  DEFAULT_GUIDANCE_HUB_ENABLED,
  GuidanceHubEnabledValue,
  GuidanceHubRoleToggles,
  GUIDANCE_HUB_ENABLED_KEY,
  GUIDANCE_CADENCE_SCHEDULE_KEY,
  normalizeCadenceSchedule,
  normalizeGuidanceHubEnabled,
} from "../../Utils/Guidance/guidanceHubSettings";

export interface GuidanceNavLinkDto {
  id: string;
  key: string;
  label: string;
  path: string;
  description: string | null;
  active: boolean;
  created_at: string;
  updated_at: string;
}

export interface GuidancePlaybookContentDto {
  id: string;
  role: string;
  cadence: string;
  cadence_tag?: string | null;
  heading: string | null;
  time: string | null;
  nav: string | null;
  what: string[];
  lookfor: string[];
  links: string[];
  agenda?: Array<Record<string, unknown>> | null;
  updated_at: string;
}

/** Alias for transitional imports from prototype code. */
export type GuidancePlaybookLinksDto = GuidancePlaybookContentDto;

export interface GuidanceRhythmProfileDto {
  id: string;
  role: string;
  role_label: string;
  role_tagline: string | null;
  closing_headline: string | null;
  closing_bullets: string[];
  updated_at: string;
}

export interface GuidanceRhythmCadenceDto {
  id: string;
  role: string;
  cadence: string;
  time: string | null;
  bullets: string[];
  updated_at: string;
}

interface GuidanceState {
  navLinks: GuidanceNavLinkDto[];
  navLinksLoading: boolean;
  navLinksError: string | null;
  playbookLinks: GuidancePlaybookContentDto[];
  playbookLinksLoading: boolean;
  playbookLinksError: string | null;
  rhythmProfiles: GuidanceRhythmProfileDto[];
  rhythmCadences: GuidanceRhythmCadenceDto[];
  rhythmLoading: boolean;
  rhythmError: string | null;
  savingKey: string | null;
  hubEnabled: boolean;
  hubEnabledRoles: GuidanceHubRoleToggles;
  hubEnabledLoaded: boolean;
  hubEnabledSaving: boolean;
  cadenceSchedule: CadenceSchedule;
  cadenceScheduleLoaded: boolean;
  cadenceScheduleSaving: boolean;
  previewDayOverride: number | null;
  previewFirstWeekOverride: boolean;
  previewFirstQuarterOverride: boolean;
}

const initialState: GuidanceState = {
  navLinks: [],
  navLinksLoading: false,
  navLinksError: null,
  hubEnabled: false,
  hubEnabledRoles: DEFAULT_GUIDANCE_HUB_ENABLED.roles,
  hubEnabledLoaded: false,
  hubEnabledSaving: false,
  cadenceSchedule: DEFAULT_CADENCE_SCHEDULE,
  cadenceScheduleLoaded: false,
  cadenceScheduleSaving: false,
  previewDayOverride: null,
  previewFirstWeekOverride: false,
  previewFirstQuarterOverride: false,
  playbookLinks: [],
  playbookLinksLoading: false,
  playbookLinksError: null,
  rhythmProfiles: [],
  rhythmCadences: [],
  rhythmLoading: false,
  rhythmError: null,
  savingKey: null,
};

const getErrorMessage = (error: unknown, fallback: string): string => {
  const err = error as {
    response?: {
      data?: {
        detail?: string;
        message?: string;
        response?: { message?: string };
      };
    };
    message?: string;
  };
  if (err?.message) {
    try {
      const parsed = JSON.parse(err.message) as { message?: string };
      if (parsed?.message) return parsed.message;
    } catch {
      /* not JSON */
    }
  }
  return (
    err?.response?.data?.response?.message ||
    err?.response?.data?.detail ||
    err?.response?.data?.message ||
    err?.message ||
    fallback
  );
};

const unwrapContent = <T>(payload: unknown): T => {
  if (payload && typeof payload === "object" && "content" in payload) {
    return (payload as { content: T }).content;
  }
  return payload as T;
};

const unwrapSettingsBundle = (
  payload: unknown
): {
  enabled?: unknown;
  schedule?: unknown;
} => {
  const content = unwrapContent<Record<string, unknown>>(payload) || {};
  return {
    enabled: content[GUIDANCE_HUB_ENABLED_KEY],
    schedule: content[GUIDANCE_CADENCE_SCHEDULE_KEY],
  };
};

export const fetchGuidanceNavLinks = createAsyncThunk(
  "guidance/fetchNavLinks",
  async (_: void, { rejectWithValue }) => {
    try {
      const res = await get(API_URLS.fetchGuidanceNavLinks());
      return unwrapContent<GuidanceNavLinkDto[]>(res) || [];
    } catch (error: unknown) {
      return rejectWithValue(
        getErrorMessage(error, "Failed to fetch nav links")
      );
    }
  }
);

export const createGuidanceNavLink = createAsyncThunk(
  "guidance/createNavLink",
  async (
    body: {
      key: string;
      label: string;
      path: string;
      description?: string;
      active?: boolean;
    },
    { rejectWithValue }
  ) => {
    try {
      const res = await post(API_URLS.createGuidanceNavLink(), body);
      return unwrapContent<GuidanceNavLinkDto>(res);
    } catch (error: unknown) {
      return rejectWithValue(getErrorMessage(error, "Failed to add nav link"));
    }
  }
);

export const updateGuidanceNavLink = createAsyncThunk(
  "guidance/updateNavLink",
  async (
    params: {
      key: string;
      fields: Partial<
        Pick<GuidanceNavLinkDto, "label" | "path" | "description" | "active">
      >;
    },
    { rejectWithValue }
  ) => {
    try {
      const res = await put(
        API_URLS.updateGuidanceNavLink(params.key),
        params.fields
      );
      return unwrapContent<GuidanceNavLinkDto>(res);
    } catch (error: unknown) {
      return rejectWithValue(
        getErrorMessage(error, "Failed to update nav link")
      );
    }
  }
);

export const fetchGuidancePlaybookLinks = createAsyncThunk(
  "guidance/fetchPlaybookLinks",
  async (role: string | void, { rejectWithValue }) => {
    try {
      const res = await get(
        API_URLS.fetchGuidancePlaybooks(
          typeof role === "string" ? role : undefined
        )
      );
      return unwrapContent<GuidancePlaybookContentDto[]>(res) || [];
    } catch (error: unknown) {
      return rejectWithValue(
        getErrorMessage(error, "Failed to fetch playbook content")
      );
    }
  }
);

export const updateGuidancePlaybookLinks = createAsyncThunk(
  "guidance/updatePlaybookLinks",
  async (
    params: {
      role: string;
      cadence: string;
      cadence_tag?: string | null;
      heading: string;
      time: string;
      nav?: string | null;
      what: string[];
      lookfor: string[];
      links: string[];
      agenda?: Array<Record<string, unknown>> | null;
    },
    { rejectWithValue }
  ) => {
    try {
      const { role, cadence, ...body } = params;
      const res = await put(
        API_URLS.updateGuidancePlaybook(role, cadence),
        body
      );
      return unwrapContent<GuidancePlaybookContentDto>(res);
    } catch (error: unknown) {
      return rejectWithValue(
        getErrorMessage(error, "Failed to update playbook content")
      );
    }
  }
);

export const fetchGuidanceRhythm = createAsyncThunk(
  "guidance/fetchRhythm",
  async (_: void, { rejectWithValue }) => {
    try {
      const [profilesRes, cadencesRes] = await Promise.all([
        get(API_URLS.fetchGuidanceRhythmProfiles()),
        get(API_URLS.fetchGuidanceRhythmCadences()),
      ]);
      return {
        profiles: unwrapContent<GuidanceRhythmProfileDto[]>(profilesRes) || [],
        cadences: unwrapContent<GuidanceRhythmCadenceDto[]>(cadencesRes) || [],
      };
    } catch (error: unknown) {
      return rejectWithValue(
        getErrorMessage(error, "Failed to fetch rhythm content")
      );
    }
  }
);

export const updateGuidanceRhythmProfile = createAsyncThunk(
  "guidance/updateRhythmProfile",
  async (
    params: {
      role: string;
      role_label: string;
      role_tagline?: string | null;
      closing_headline?: string | null;
      closing_bullets: string[];
    },
    { rejectWithValue }
  ) => {
    try {
      const { role, ...body } = params;
      const res = await put(API_URLS.updateGuidanceRhythmProfile(role), body);
      return unwrapContent<GuidanceRhythmProfileDto>(res);
    } catch (error: unknown) {
      return rejectWithValue(
        getErrorMessage(error, "Failed to update rhythm profile")
      );
    }
  }
);

export const updateGuidanceRhythmCadence = createAsyncThunk(
  "guidance/updateRhythmCadence",
  async (
    params: {
      role: string;
      cadence: string;
      time?: string | null;
      bullets: string[];
    },
    { rejectWithValue }
  ) => {
    try {
      const { role, cadence, ...body } = params;
      const res = await put(
        API_URLS.updateGuidanceRhythmCadence(role, cadence),
        body
      );
      return unwrapContent<GuidanceRhythmCadenceDto>(res);
    } catch (error: unknown) {
      return rejectWithValue(
        getErrorMessage(error, "Failed to update rhythm cadence")
      );
    }
  }
);

export const fetchGuidanceHubEnabled = createAsyncThunk(
  "guidance/fetchHubEnabled",
  async (_: void, { rejectWithValue }) => {
    try {
      const res = await get(API_URLS.fetchGuidanceHubSettings());
      const { enabled } = unwrapSettingsBundle(res);
      return normalizeGuidanceHubEnabled(enabled);
    } catch (error: unknown) {
      return rejectWithValue(getErrorMessage(error, "Failed to fetch"));
    }
  }
);

export const updateGuidanceHubEnabled = createAsyncThunk(
  "guidance/updateHubEnabled",
  async (value: GuidanceHubEnabledValue, { rejectWithValue }) => {
    try {
      await put(API_URLS.updateGuidanceHubSetting(GUIDANCE_HUB_ENABLED_KEY), {
        value,
      });
      return value;
    } catch (error: unknown) {
      return rejectWithValue(getErrorMessage(error, "Failed to save"));
    }
  }
);

export const fetchGuidanceCadenceSchedule = createAsyncThunk(
  "guidance/fetchCadenceSchedule",
  async (_: void, { rejectWithValue }) => {
    try {
      const res = await get(API_URLS.fetchGuidanceHubSettings());
      const { schedule } = unwrapSettingsBundle(res);
      return normalizeCadenceSchedule(schedule);
    } catch (error: unknown) {
      return rejectWithValue(getErrorMessage(error, "Failed to fetch"));
    }
  }
);

export const updateGuidanceCadenceSchedule = createAsyncThunk(
  "guidance/updateCadenceSchedule",
  async (value: CadenceSchedule, { rejectWithValue }) => {
    try {
      await put(
        API_URLS.updateGuidanceHubSetting(GUIDANCE_CADENCE_SCHEDULE_KEY),
        { value }
      );
      return value;
    } catch (error: unknown) {
      return rejectWithValue(getErrorMessage(error, "Failed to save"));
    }
  }
);

const guidanceSlice = createSlice({
  name: "guidance",
  initialState,
  reducers: {
    setGuidancePreviewDay: (state, action: PayloadAction<number | null>) => {
      state.previewDayOverride = action.payload;
    },
    setGuidancePreviewFirstWeek: (state, action: PayloadAction<boolean>) => {
      state.previewFirstWeekOverride = action.payload;
    },
    setGuidancePreviewFirstQuarter: (state, action: PayloadAction<boolean>) => {
      state.previewFirstQuarterOverride = action.payload;
    },
    resetGuidancePreview: state => {
      state.previewDayOverride = null;
      state.previewFirstWeekOverride = false;
      state.previewFirstQuarterOverride = false;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchGuidanceNavLinks.pending, state => {
        state.navLinksLoading = true;
        state.navLinksError = null;
      })
      .addCase(fetchGuidanceNavLinks.fulfilled, (state, action) => {
        state.navLinksLoading = false;
        state.navLinks = action.payload;
      })
      .addCase(fetchGuidanceNavLinks.rejected, (state, action) => {
        state.navLinksLoading = false;
        state.navLinksError =
          (action.payload as string) ||
          action.error.message ||
          "Failed to fetch nav links";
      })
      .addCase(createGuidanceNavLink.pending, state => {
        state.savingKey = "__new__";
      })
      .addCase(createGuidanceNavLink.fulfilled, (state, action) => {
        state.savingKey = null;
        if (action.payload) state.navLinks.push(action.payload);
      })
      .addCase(createGuidanceNavLink.rejected, (state, action) => {
        state.savingKey = null;
        state.navLinksError =
          (action.payload as string) ||
          action.error.message ||
          "Failed to add nav link";
      })
      .addCase(updateGuidanceNavLink.pending, (state, action) => {
        state.savingKey = action.meta.arg.key;
      })
      .addCase(updateGuidanceNavLink.fulfilled, (state, action) => {
        state.savingKey = null;
        if (!action.payload) return;
        const idx = state.navLinks.findIndex(l => l.key === action.payload.key);
        if (idx >= 0) state.navLinks[idx] = action.payload;
      })
      .addCase(updateGuidanceNavLink.rejected, (state, action) => {
        state.savingKey = null;
        state.navLinksError =
          (action.payload as string) ||
          action.error.message ||
          "Failed to update nav link";
      })
      .addCase(fetchGuidancePlaybookLinks.pending, state => {
        state.playbookLinksLoading = true;
        state.playbookLinksError = null;
      })
      .addCase(fetchGuidancePlaybookLinks.fulfilled, (state, action) => {
        state.playbookLinksLoading = false;
        state.playbookLinks = action.payload;
      })
      .addCase(fetchGuidancePlaybookLinks.rejected, (state, action) => {
        state.playbookLinksLoading = false;
        state.playbookLinksError =
          (action.payload as string) ||
          action.error.message ||
          "Failed to fetch playbook content";
      })
      .addCase(updateGuidancePlaybookLinks.pending, (state, action) => {
        state.savingKey = `${action.meta.arg.role}:${action.meta.arg.cadence}`;
      })
      .addCase(updateGuidancePlaybookLinks.fulfilled, (state, action) => {
        state.savingKey = null;
        if (!action.payload) return;
        const idx = state.playbookLinks.findIndex(
          p =>
            p.role === action.payload.role &&
            p.cadence === action.payload.cadence
        );
        if (idx >= 0) state.playbookLinks[idx] = action.payload;
        else state.playbookLinks.push(action.payload);
      })
      .addCase(updateGuidancePlaybookLinks.rejected, (state, action) => {
        state.savingKey = null;
        state.playbookLinksError =
          (action.payload as string) ||
          action.error.message ||
          "Failed to update playbook content";
      })
      .addCase(fetchGuidanceRhythm.pending, state => {
        state.rhythmLoading = true;
        state.rhythmError = null;
      })
      .addCase(fetchGuidanceRhythm.fulfilled, (state, action) => {
        state.rhythmLoading = false;
        state.rhythmProfiles = action.payload.profiles;
        state.rhythmCadences = action.payload.cadences;
      })
      .addCase(fetchGuidanceRhythm.rejected, (state, action) => {
        state.rhythmLoading = false;
        state.rhythmError =
          (action.payload as string) ||
          action.error.message ||
          "Failed to fetch rhythm content";
      })
      .addCase(updateGuidanceRhythmProfile.pending, (state, action) => {
        state.savingKey = `rhythm-profile:${action.meta.arg.role}`;
      })
      .addCase(updateGuidanceRhythmProfile.fulfilled, (state, action) => {
        state.savingKey = null;
        if (!action.payload) return;
        const idx = state.rhythmProfiles.findIndex(
          p => p.role === action.payload.role
        );
        if (idx >= 0) state.rhythmProfiles[idx] = action.payload;
        else state.rhythmProfiles.push(action.payload);
      })
      .addCase(updateGuidanceRhythmProfile.rejected, (state, action) => {
        state.savingKey = null;
        state.rhythmError =
          (action.payload as string) ||
          action.error.message ||
          "Failed to update rhythm profile";
      })
      .addCase(updateGuidanceRhythmCadence.pending, (state, action) => {
        state.savingKey = `rhythm:${action.meta.arg.role}:${action.meta.arg.cadence}`;
      })
      .addCase(updateGuidanceRhythmCadence.fulfilled, (state, action) => {
        state.savingKey = null;
        if (!action.payload) return;
        const idx = state.rhythmCadences.findIndex(
          c =>
            c.role === action.payload.role &&
            c.cadence === action.payload.cadence
        );
        if (idx >= 0) state.rhythmCadences[idx] = action.payload;
        else state.rhythmCadences.push(action.payload);
      })
      .addCase(updateGuidanceRhythmCadence.rejected, (state, action) => {
        state.savingKey = null;
        state.rhythmError =
          (action.payload as string) ||
          action.error.message ||
          "Failed to update rhythm cadence";
      })
      .addCase(fetchGuidanceHubEnabled.fulfilled, (state, action) => {
        state.hubEnabled = action.payload.enabled;
        state.hubEnabledRoles = action.payload.roles;
        state.hubEnabledLoaded = true;
      })
      .addCase(fetchGuidanceHubEnabled.rejected, state => {
        state.hubEnabled = DEFAULT_GUIDANCE_HUB_ENABLED.enabled;
        state.hubEnabledRoles = DEFAULT_GUIDANCE_HUB_ENABLED.roles;
        state.hubEnabledLoaded = true;
      })
      .addCase(updateGuidanceHubEnabled.pending, state => {
        state.hubEnabledSaving = true;
      })
      .addCase(updateGuidanceHubEnabled.fulfilled, (state, action) => {
        state.hubEnabledSaving = false;
        state.hubEnabled = action.payload.enabled;
        state.hubEnabledRoles = action.payload.roles;
      })
      .addCase(updateGuidanceHubEnabled.rejected, state => {
        state.hubEnabledSaving = false;
      })
      .addCase(fetchGuidanceCadenceSchedule.fulfilled, (state, action) => {
        state.cadenceSchedule = action.payload;
        state.cadenceScheduleLoaded = true;
      })
      .addCase(fetchGuidanceCadenceSchedule.rejected, state => {
        state.cadenceSchedule = DEFAULT_CADENCE_SCHEDULE;
        state.cadenceScheduleLoaded = true;
      })
      .addCase(updateGuidanceCadenceSchedule.pending, state => {
        state.cadenceScheduleSaving = true;
      })
      .addCase(updateGuidanceCadenceSchedule.fulfilled, (state, action) => {
        state.cadenceScheduleSaving = false;
        state.cadenceSchedule = action.payload;
      })
      .addCase(updateGuidanceCadenceSchedule.rejected, state => {
        state.cadenceScheduleSaving = false;
      });
  },
});

export const {
  setGuidancePreviewDay,
  setGuidancePreviewFirstWeek,
  setGuidancePreviewFirstQuarter,
  resetGuidancePreview,
} = guidanceSlice.actions;

export default guidanceSlice.reducer;
